import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastrService } from '@shared/services/toastr.service';
import { distinctUntilChanged, of, Subject, Subscription, switchMap } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { AuthService } from '@shared/services/auth.service';
import { Contract, ContractInvoicingStats } from '@shared/models/contract';
import { PageRequest } from '@shared/page';
import { AdminContractService, ContractType } from '@shared/services/admin-contract.service';
import { ADD_ROUTE, ADMIN_PREFIX } from 'app/app-routing.constants';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ActiveToast } from 'ngx-toastr';
import { ModalBasicComponent } from '@shared/components/modal-basic/modal-basic.component';
import { isEqual } from 'lodash';
import { ORGANIZATIONS_ROUTE } from '../admin-routing.module';
import { ContractsFiltersComponent } from './contracts-filters/contracts-filters.component';
import { NgFor, NgIf } from '@angular/common';
import { BadgeOrganizationComponent } from '@shared/components/badge-organization/badge-organization.component';
import { TimestampCellComponent } from '@shared/components/timestamp-cell/timestamp-cell.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { ModalBasicComponent as ModalBasicComponent_1 } from '../../../shared/components/modal-basic/modal-basic.component';

@Component({
  selector: 'app-contracts',
  templateUrl: './contracts.component.html',
  styleUrls: ['./contracts.component.scss'],
  imports: [
    RouterLink,
    ContractsFiltersComponent,
    NgIf,
    NgFor,
    BadgeOrganizationComponent,
    TimestampCellComponent,
    NgxPaginationModule,
    ModalBasicComponent_1,
    TranslatePipe,
  ],
})
export class ContractsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('remove') removeModal: ModalBasicComponent;

  contract: Partial<Contract> | null;
  contracts: (Contract & { nbSites: number | null } & Partial<ContractInvoicingStats>)[] = [];

  filters$: Subject<ContractFilters> = new Subject();
  filters: ContractFilters;

  page = 1;
  pageSize: number = 20;
  totalItems: number = 0;

  private subscriptions$: Subscription = new Subscription();

  constructor(
    private translate: TranslateService,
    private toastr: ToastrService,
    public auth: AuthService,
    private admin: AdminContractService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.readQueryParams();
  }

  ngAfterViewInit(): void {
    this.subscriptions$.add(
      this.filters$.pipe(distinctUntilChanged(isEqual), debounceTime(200)).subscribe((filters) => {
        this.filters = filters;
        this.page = 1;
        this.setQueryParams();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
  }

  changePage(page: number) {
    this.page = page;
    this.setQueryParams();
  }

  private readQueryParams(): void {
    this.subscriptions$.add(
      this.route.queryParamMap.subscribe((queryParams) => {
        this.filters = {
          filter: queryParams.get('filter') ?? '',
          type: queryParams.getAll('type') as any,
        };
        this.page = parseInt(queryParams.get('page') ?? '1');
        this.getContracts();
      }),
    );
  }

  private setQueryParams(): void {
    this.router.navigate([], {
      queryParams: {
        filter: this.filters.filter,
        type: this.filters.type,
        page: this.page,
      },
    });
  }

  private getPageRequest(): PageRequest {
    return { number: this.page - 1, size: this.pageSize };
  }

  private getContracts() {
    return this.admin
      .getContracts(this.filters, this.getPageRequest())
      .pipe(
        switchMap((page) =>
          page.content.length
            ? this.admin.getInvoicingStats(page.content.map((contract) => contract.number)).pipe(
                map((stats) => ({
                  ...page,
                  content: page.content.map((contract) => ({
                    ...contract,
                    nbTotalSites: stats[contract.number]?.nbTotalSites || 0,
                    nbInvoicedSites: stats[contract.number]?.nbInvoicedSites || 0,
                    nbInvoicedClusters: stats[contract.number]?.nbInvoicedClusters || 0,
                    nbOrganizations: stats[contract.number]?.nbOrganizations || 0,
                  })),
                })),
              )
            : of(page),
        ),
      )
      .subscribe((page) => {
        this.totalItems = page.totalElements;
        this.contracts = page.content.map((contract) => {
          const nbSites =
            contract.nbSitesAdvanced || contract.nbSitesExpert
              ? (contract.nbSitesAdvanced ?? 0) + (contract.nbSitesExpert ?? 0)
              : 0;
          return { ...contract, nbSites };
        });
      });
  }

  onDataFilterChange(event: ContractFilters) {
    this.filters$.next(event);
  }

  modalRemove(p: Contract & Partial<ContractInvoicingStats>): void {
    this.removeModal.show();
    this.contract = { ...p };
  }

  async removeContract(number: string): Promise<ActiveToast<any>> | never {
    try {
      await this.admin.deleteContract(number);
    } catch (error) {
      return this.toastr.error(this.translate.instant(error?.error?.error || 'Erreur'));
    }

    this.toastr.success('SuppressionSucces');
    this.removeModal.hide();
    this.getContracts();
  }

  protected readonly ADD_ROUTE = ADD_ROUTE;
  protected readonly ADMIN_PREFIX = ADMIN_PREFIX;
  protected readonly ORGANIZATIONS_ROUTE = ORGANIZATIONS_ROUTE;
}

export type ContractFilters = {
  filter: string;
  type: ContractType[];
};
